import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AddPlanDomainResponse, EnrollmentDto } from "@/types/api";

/**
 * The account's purchased plans, each with the domains bought under it and
 * that plan's pool usage. Powers the Overview plan list + the plan detail page.
 */
export function useEnrollments() {
  return useQuery({
    queryKey: ["accounts", "me", "enrollments"],
    staleTime: 30_000,
    queryFn: ({ signal }) =>
      api.get<EnrollmentDto[]>("/api/accounts/me/enrollments", signal)
  });
}

export function useEnrollment(subscriptionId: number | undefined) {
  const q = useEnrollments();
  const enrollment = q.data?.find(e => e.subscription_id === subscriptionId);
  return { ...q, enrollment };
}

/**
 * Connect a domain the customer already owns to one of their plans
 * (free — UseExisting mode; register/transfer go through checkout).
 * Returns the nameservers they must set at their current registrar.
 */
export function useAddDomainToPlan(subscriptionId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domainName: string) =>
      api.post<AddPlanDomainResponse>(
        `/api/accounts/me/enrollments/${subscriptionId}/domains`,
        { domain_name: domainName }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
      qc.invalidateQueries({ queryKey: ["containers"] });
    }
  });
}
